import {
  Button,
  Grid,
  Box,
  Tooltip,
  useTheme,
  Typography,
  Chip,
  alpha,
} from "@mui/material";
import { useSelector } from "react-redux";
import PageTitle from "../components/PageTitle";
import { useTranslation } from "react-i18next";
import { Edit, ArrowBack, Refresh } from "@mui/icons-material";
import { useState, useMemo } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useBillingCheck } from "../hooks/useBillingCheck";
import DocumentTitle from "../components/DocumentTitle";
import LabelEditDialog from "../components/Dialogs/labelsEdit";
import useLabels, { useLabelDevices } from "../hooks/useLabels";
import DeviceCard from "../components/Cards/Device";
import { useDevices } from "../hooks/queries";

export default function Label() {
  useBillingCheck();

  const { id } = useParams();
  const navigate = useNavigate();
  const theme = useTheme();
  const { t } = useTranslation("common");

  const selectedOrganisation = useSelector(
    (state) => state.selectedOrganisation
  );
  const isMobile = useSelector((state) => state.isMobile);

  const { data: allLabels = [] } = useLabels(selectedOrganisation?.id);
  const label = allLabels.find((l) => String(l.id) === String(id));

  const { data: labelDevices = [], refetch: refetchLabelDevices } =
    useLabelDevices(selectedOrganisation ? id : null);

  const { data: devices = [] } = useDevices(selectedOrganisation?.id);

  // Map label device references to full device objects, falling back to the label device data itself
  const fullDevices = useMemo(() => {
    if (!labelDevices.length) return [];
    if (!devices.length) return labelDevices;
    return labelDevices
      .map(
        (ld) =>
          devices.find((d) => d.id === ld.id || d.id === ld.device_id) || ld
      )
      .filter(Boolean);
  }, [labelDevices, devices]);

  const [editOpen, setEditOpen] = useState(false);

  const handleRefresh = () => {
    refetchLabelDevices();
  };

  return (
    <>
      <DocumentTitle
        title={label?.name || t("labels.index")}
        selectedOrganisation={selectedOrganisation}
      />

      <Grid container spacing={2}>
        <Grid size={{ xs: 12 }}>
          <Box
            display="flex"
            flexDirection="row"
            justifyContent={isMobile ? "flex-start" : "flex-end"}
            alignItems="center"
          >
            <PageTitle
              theme={theme}
              title={
                <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
                  {label?.name || t("labels.index")}
                  {label?.color && (
                    <Chip
                      size="small"
                      label={label.name}
                      sx={{
                        backgroundColor: alpha(label.color, 0.15),
                        color: label.color,
                        fontWeight: 600,
                        borderRadius: "16px",
                        fontSize: "0.75rem",
                      }}
                    />
                  )}
                </Box>
              }
              subTitle={t("common.labels_in_organisation", {
                count: fullDevices.length,
              })}
            />

            <Tooltip title={t("common.back")} placement="top" arrow>
              <Button
                sx={{ mr: 1 }}
                variant="outlined"
                color={theme.palette.mode === "dark" ? "primary" : "secondary"}
                onClick={() => navigate("/label")}
              >
                <ArrowBack />
              </Button>
            </Tooltip>

            <Tooltip title={t("labels.edit")} placement="top" arrow>
              <Button
                sx={{ mr: 1 }}
                variant="outlined"
                color={theme.palette.mode === "dark" ? "primary" : "secondary"}
                onClick={() => setEditOpen(true)}
              >
                <Edit />
              </Button>
            </Tooltip>

            <Tooltip title={t("common.refresh")} placement="top" arrow>
              <Button
                variant="outlined"
                color={theme.palette.mode === "dark" ? "primary" : "secondary"}
                onClick={handleRefresh}
              >
                <Refresh />
              </Button>
            </Tooltip>
          </Box>
        </Grid>

        {fullDevices.map((device, index) => (
          <DeviceCard
            key={`${device.id || device.dev_eui || "device"}-${index}`}
            device={device}
          />
        ))}
        {fullDevices.length === 0 && (
          <Grid size={{ xs: 12 }}>
            <Box
              sx={{
                textAlign: "center",
                py: 6,
              }}
            >
              <Typography variant="body1" color="text.secondary">
                {t("labels.no_devices")}
              </Typography>
            </Box>
          </Grid>
        )}
      </Grid>

      {label && (
        <LabelEditDialog
          open={editOpen}
          handleClose={() => {
            setEditOpen(false);
            handleRefresh();
          }}
          label={label}
        />
      )}
    </>
  );
}
